<template>
  <div class="configuration-tab">
    <app-loading :loading="isLoading" is-background />

    <ValidationObserver v-if="!isLoading" v-slot="v">
      <b-form id="edit-award-form" @submit.prevent="v.handleSubmit(onSubmit)">
        <div class="form-container">
          <!-- Basic Details -->
          <basic-details-section
            :name="awardForm.name"
            :selected-division-ids="selectedDivisionIds"
            :division-options="divisionOptions"
            @update:name="awardForm.name = $event"
            @update:selectedDivisionIds="onDivisionsChange"
          >
            <template #after-divisions>
              <hr />

              <div class="company-values-container">
                <div class="label-row">
                  <div class="label">
                    Add your company values (optional)
                    <small>
                      Tag relevant values to help employees understand what behaviours this award recognises. You can
                      use our suggestions or customise them to match your organisation's values.
                    </small>
                  </div>
                  <b-form-checkbox v-model="companyValuesEnabled" switch />
                </div>
                <div v-if="companyValuesEnabled">
                  <create-company-values-modal v-model="companyValues" />
                </div>
              </div>
            </template>
          </basic-details-section>

          <!-- Award Details -->
          <div>
            <h4>Award details</h4>
            <sme-card large>
              <monetary-reward-section
                :enabled="monetaryRewardEnabled"
                :currency-symbol="currencySymbol"
                :amounts="awardValueMinor"
                :default-amount-minor="defaultAwardValueMinor"
                :amount-error="awardValueError"
                :redemption-options="selectedRedemptionOptions"
                :redemption-error="redemptionSelectionError"
                :expiration-months="expirationMonths"
                @update:enabled="monetaryRewardEnabled = $event"
                @update:amounts="awardValueMinor = $event"
                @update:defaultAmountMinor="onDefaultAwardValueMinorChange"
                @update:redemptionOptions="selectedRedemptionOptions = $event"
                @update:expirationMonths="expirationMonths = $event"
              >
                <template #after-amounts>
                  <budget-settings-section
                    v-if="monetaryRewardEnabled"
                    :currency-symbol="currencySymbol"
                    :amount="rewardBudgetAmount"
                    :cadence="rewardBudgetCadence"
                    :year-start-month="budgetYearStartMonth"
                    :year-start-day="budgetYearStartDay"
                    :year-start-day-options="budgetYearStartDayOptions"
                    :year-start-disabled="budgetYearStartDisabled"
                    :year-start-month-rules="budgetYearStartMonthRules"
                    :year-start-day-rules="budgetYearStartDayRules"
                    @update:amount="rewardBudgetAmount = $event"
                    @update:cadence="rewardBudgetCadence = $event"
                    @update:yearStartMonth="budgetYearStartMonth = $event"
                    @update:yearStartDay="budgetYearStartDay = $event"
                  />
                </template>
              </monetary-reward-section>
            </sme-card>
          </div>

          <!-- Nominations & Approvals -->
          <div v-if="monetaryRewardEnabled">
            <h4>Nominations & Approvals</h4>
            <sme-card large>
              <div class="label-permissions-and-approvals">
                Permissions and approvals
                <small>
                  This award is <span class="highlighted-text">open</span>: anyone can send recognition. Recognitions
                  with monetary rewards will require approval from a reviewer.
                </small>
              </div>

              <div class="approver-container">
                <multi-select
                  id="approvers-select"
                  :value="selectedApproverIds"
                  :options="employeeOptions"
                  placeholder="Select who can approve this award"
                  :searchable="true"
                  search-placeholder="Search by name or email"
                  :disabled="employeesLoading || approverOptionsLoading"
                  :select-all="false"
                  select-all-text="No approvers available"
                  class="mb-0"
                  @input="onApproversChange"
                >
                  <template #label>
                    <div class="label">Who can approve?</div>
                  </template>
                </multi-select>
                <small v-if="approverSelectionError" class="text-danger">{{ approverSelectionError }}</small>
              </div>
            </sme-card>
          </div>

          <!-- Award Status - Commented out until backend implements support
          <div>
            <h4>Award status</h4>
            <sme-card large>
              <div class="label-row">
                <div class="label">
                  Award active
                  <small
                    >When disabled, this award will not be visible to employees and no new nominations can be
                    made.</small
                  >
                </div>
                <b-form-checkbox v-model="isActive" switch />
              </div>
            </sme-card>
          </div>
          -->
        </div>

        <!-- Footer -->
        <div class="form-footer">
          <small v-if="errorMessage" class="text-danger mr-3">{{ errorMessage }}</small>
          <b-button variant="outline-secondary" class="mr-2" :disabled="saving" @click="resetForm"> Reset </b-button>
          <b-button type="submit" variant="primary" :disabled="saving || !hasChanges">
            <b-spinner v-if="saving" class="mr-2" small />
            {{ saving ? 'Saving...' : 'Save changes' }}
          </b-button>
        </div>
      </b-form>
    </ValidationObserver>
  </div>
</template>

<script setup>
import { ValidationObserver } from 'vee-validate';
import { ref, computed, watch, onMounted } from 'vue';
import ApiClient from '@/ApiClient';
import { RECOGNITION_APPROVAL_TYPES } from '@/pages/recognition/constants';
import { useAwardForm } from '@/pages/recognition/composables/useAwardForm';
import AppLoading from '@/components/AppLoading.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import CreateCompanyValuesModal from '@/pages/recognition/components/CreateCompanyValuesModal.vue';
import useToast from '@/composables/useToast';
import SmeCard from '@/components/atoms/SmeCard.vue';
import BasicDetailsSection from '@/pages/recognition/components/BasicDetailsSection.vue';
import MonetaryRewardSection from '@/pages/recognition/components/MonetaryRewardSection.vue';
import BudgetSettingsSection from '@/pages/recognition/components/BudgetSettingsSection.vue';

const props = defineProps({
  program: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['updated']);

// Use shared composable
const {
  currencySymbol,
  awardForm,
  selectedDivisionIds,
  divisionOptions,
  enabledDivisions,
  monetaryRewardEnabled,
  awardValueMinor,
  awardValueError,
  defaultAwardValueMinor,
  selectedRedemptionOptions,
  redemptionSelectionError,
  expirationMonths,
  rewardBudgetAmount,
  rewardBudgetCadence,
  budgetYearStartMonth,
  budgetYearStartDay,
  budgetYearStartDayOptions,
  budgetYearStartDisabled,
  budgetYearStartMonthRules,
  budgetYearStartDayRules,
  saving,
  errorMessage,
  onDefaultAwardValueMinorChange,
  loadDivisions,
  initBudgetYearStart,
  setupCommonWatchers,
  resetMonetaryRewardFields,
  buildBaseProperties,
  getBudgetMinor,
  getGrantExpiryInterval,
  persistBudgetYearStart,
  validateRedemptionOptions,
  validateAwardValues,
  validateBudgetCadence,
} = useAwardForm();

// Open-specific state
const companyValuesEnabled = ref(false);
const companyValues = ref([]);
const employees = ref([]);
const selectedApproverIds = ref([]);
const employeesLoading = ref(false);
const approverOptionsLoading = ref(false);
const eligibleApproverIds = ref(new Set());
const approverSelectionError = ref('');
const isActive = ref(true);
const isLoading = ref(true);

// Track original values for change detection
const originalValues = ref({});

const { doToast, TOAST_VARIANT } = useToast();

const employeeOptions = computed(() => {
  const allEmployees = employees.value || [];
  const selectedDivisions = selectedDivisionIds.value || [];

  if (!selectedDivisions.length) {
    return allEmployees.map(e => ({
      text: `${e.full_name} - ${e.work_email}`,
      value: e.employee_id,
    }));
  }

  const allowed = eligibleApproverIds.value || new Set();
  return allEmployees
    .filter(e => allowed.has(e.employee_id))
    .map(e => ({
      text: `${e.full_name} - ${e.work_email}`,
      value: e.employee_id,
    }));
});

// Detect if form has changes
const hasChanges = computed(() => {
  const current = getCurrentFormState();
  return JSON.stringify(current) !== JSON.stringify(originalValues.value);
});

/**
 * Parse ISO 8601 duration to months.
 * Handles both month format ("P12M" -> 12) and day format ("P360D" -> 12).
 */
const parseGrantExpiryInterval = interval => {
  if (!interval) {
    return '';
  }
  const str = String(interval);
  // Try month format first (P12M)
  const monthMatch = str.match(/^P(\d+)M$/);
  if (monthMatch) {
    return monthMatch[1];
  }
  // Try day format (P360D) - convert days to months (30 days per month)
  const dayMatch = str.match(/^P(\d+)D$/);
  if (dayMatch) {
    const days = parseInt(dayMatch[1], 10);
    const months = Math.round(days / 30);
    return months > 0 ? String(months) : '';
  }
  return '';
};

/**
 * Get current form state for comparison
 */
const getCurrentFormState = () => ({
  name: awardForm.value.name,
  selectedDivisionIds: [...(selectedDivisionIds.value || [])].sort(),
  companyValuesEnabled: companyValuesEnabled.value,
  companyValues: [...(companyValues.value || [])].sort(),
  monetaryRewardEnabled: monetaryRewardEnabled.value,
  awardValueMinor: awardValueMinor.value,
  defaultAwardValueMinor: defaultAwardValueMinor.value,
  selectedRedemptionOptions: [...(selectedRedemptionOptions.value || [])].sort(),
  expirationMonths: expirationMonths.value,
  rewardBudgetAmount: rewardBudgetAmount.value,
  rewardBudgetCadence: rewardBudgetCadence.value,
  budgetYearStartMonth: budgetYearStartMonth.value,
  budgetYearStartDay: budgetYearStartDay.value,
  selectedApproverIds: [...(selectedApproverIds.value || [])].sort(),
  isActive: isActive.value,
});

/**
 * Populate form from program data
 */
const populateFromProgram = program => {
  if (!program) {
    return;
  }

  // Basic details
  awardForm.value.name = program.program_name || '';
  isActive.value = program.is_active !== false;

  // Divisions - API returns array of UUIDs or empty array for "all"
  const divisions = program.enabled_divisions || [];
  selectedDivisionIds.value = divisions.length > 0 ? divisions : [];

  // Company values
  const programCompanyValues = program.properties?.company_values || [];
  if (programCompanyValues.length > 0) {
    companyValuesEnabled.value = true;
    companyValues.value = programCompanyValues.map(v => v.name || v);
  } else {
    companyValuesEnabled.value = false;
    companyValues.value = [];
  }

  // Monetary reward
  const hasMonetaryReward = program.properties?.award_value_minor != null;
  monetaryRewardEnabled.value = hasMonetaryReward;

  if (hasMonetaryReward) {
    awardValueMinor.value = program.properties.award_value_minor;
    defaultAwardValueMinor.value = program.properties.default_award_value_minor;

    // Redemption options
    const validGrantTypes = program.properties?.valid_grant_types || ['VOUCHER'];
    selectedRedemptionOptions.value = validGrantTypes;

    // Expiration
    expirationMonths.value = parseGrantExpiryInterval(program.grant_expiry_interval);

    // Budget
    if (program.budget_minor) {
      rewardBudgetAmount.value = (program.budget_minor / 100).toFixed(2);
    } else {
      rewardBudgetAmount.value = '';
    }
    if (program.properties?.budget_period) {
      rewardBudgetCadence.value = program.properties.budget_period;
    } else {
      rewardBudgetCadence.value = undefined;
    }

    // Approvers
    const approverGroups = program.properties?.approvers || [];
    if (approverGroups.length > 0) {
      const allApproverIds = approverGroups.flatMap(g => g.approvers || []);
      selectedApproverIds.value = allApproverIds;
    } else {
      selectedApproverIds.value = [];
    }
  } else {
    // Clear monetary reward fields when not enabled
    awardValueMinor.value = undefined;
    defaultAwardValueMinor.value = undefined;
    selectedRedemptionOptions.value = [];
    expirationMonths.value = '';
    rewardBudgetAmount.value = '';
    rewardBudgetCadence.value = undefined;
    selectedApproverIds.value = [];
  }

  // Store original values for change detection
  originalValues.value = getCurrentFormState();
};

/**
 * Reset form to original program values
 */
const resetForm = () => {
  populateFromProgram(props.program);
  errorMessage.value = '';
  approverSelectionError.value = '';
  awardValueError.value = '';
  redemptionSelectionError.value = '';
};

const refreshEligibleApprovers = async () => {
  approverOptionsLoading.value = true;
  try {
    const ids = selectedDivisionIds.value || [];

    if (!ids.length) {
      eligibleApproverIds.value = new Set((employees.value || []).map(e => e.employee_id));
      return;
    }

    const results = await Promise.all(
      ids.map(async divisionId => {
        try {
          return (await ApiClient.getDivisionEmployees(divisionId))?.data || [];
        } catch {
          return [];
        }
      }),
    );
    const union = new Set();
    results.forEach(list => {
      list.forEach(emp => union.add(emp.employee_id));
    });
    eligibleApproverIds.value = union;
  } finally {
    approverOptionsLoading.value = false;
  }
};

const buildProperties = (approverIds, redemptionMethods) => {
  const props = buildBaseProperties(redemptionMethods);

  // Set approvers to null when empty to explicitly clear them
  props.approvers = (approverIds || []).length
    ? [
        {
          name: 'Default',
          approvers: (approverIds || []).map(String),
        },
      ]
    : null;

  if (companyValuesEnabled.value) {
    const values = (companyValues.value || []).map(v => String(v || '').trim()).filter(Boolean);
    if (values.length) {
      props.company_values = values.map(name => ({ name }));
    }
  }

  return props;
};

const buildPayload = (properties, approvers) => ({
  program_name: awardForm.value.name,
  approval_type: (approvers || []).length > 0 ? RECOGNITION_APPROVAL_TYPES.AMOUNT : RECOGNITION_APPROVAL_TYPES.NONE,
  show_badge: false,
  enabled_divisions: enabledDivisions.value,
  budget_minor: getBudgetMinor(),
  grant_expiry_interval: getGrantExpiryInterval(),
  is_active: isActive.value,
  properties,
});

const onSubmit = async () => {
  errorMessage.value = '';
  saving.value = true;
  const approvers = monetaryRewardEnabled.value ? selectedApproverIds.value || [] : [];
  const redemptionMethods = monetaryRewardEnabled.value ? selectedRedemptionOptions.value || [] : [];

  try {
    if (!validateRedemptionOptions(redemptionMethods)) {
      saving.value = false;
      return;
    }

    // Require at least one approver when monetary rewards are enabled
    if (monetaryRewardEnabled.value && (!approvers || approvers.length === 0)) {
      approverSelectionError.value = 'Please select at least one approver.';
      saving.value = false;
      return;
    }
    approverSelectionError.value = '';

    if (!validateAwardValues()) {
      saving.value = false;
      return;
    }

    if (!validateBudgetCadence()) {
      saving.value = false;
      return;
    }

    const approverIds = (approvers || []).map(String);
    const properties = buildProperties(approverIds, redemptionMethods);
    const payload = buildPayload(properties, approvers);

    const budgetResult = await persistBudgetYearStart();
    if (!budgetResult.success) {
      errorMessage.value = budgetResult.error;
      saving.value = false;
      return;
    }

    await ApiClient.updateRecognitionProgram(props.program.recognition_award_program_id, payload);

    doToast('Success', 'Award updated successfully', TOAST_VARIANT.SUCCESS);

    // Update original values to reflect saved state
    originalValues.value = getCurrentFormState();

    emit('updated');
  } catch (e) {
    errorMessage.value = e?.variables?.message || e?.message || 'Failed to update award';
  } finally {
    saving.value = false;
  }
};

const onDivisionsChange = async ids => {
  selectedDivisionIds.value = ids;
  await refreshEligibleApprovers();
  const allowed = eligibleApproverIds.value || new Set();
  selectedApproverIds.value = (selectedApproverIds.value || []).filter(id => allowed.has(id));
};

const onApproversChange = ids => {
  selectedApproverIds.value = ids;
};

watch(selectedApproverIds, newVal => {
  if (approverSelectionError.value && Array.isArray(newVal) && newVal.length > 0) {
    approverSelectionError.value = '';
  }
});

watch(monetaryRewardEnabled, enabled => {
  if (!enabled) {
    resetMonetaryRewardFields();
    selectedApproverIds.value = [];
    approverSelectionError.value = '';
  }
});

onMounted(async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      loadDivisions(),
      (async () => {
        try {
          employeesLoading.value = true;
          employees.value = (await ApiClient.getAllEmployees())?.data || [];
        } catch {
          // silently ignore
        } finally {
          employeesLoading.value = false;
        }
      })(),
    ]);
    initBudgetYearStart();
    setupCommonWatchers();
    populateFromProgram(props.program);
    await refreshEligibleApprovers();
  } finally {
    isLoading.value = false;
  }
});

// Watch for program prop changes (e.g., when refreshed from parent)
watch(
  () => props.program,
  newProgram => {
    if (newProgram) {
      populateFromProgram(newProgram);
    }
  },
);
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';

.configuration-tab {
  position: relative;
  min-height: 200px;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--palette-color-default-lighten-90);
}
</style>
